/**
 * A project document laid out in OUTPUT time.
 *
 * One plan, two consumers: the export composites from it, and the raw-project
 * manifest is built from it. That is deliberate — the whole reason the earlier
 * manifest states positions rather than letting the server recompute them is that
 * two independent layouts of the same timeline drift apart. Two independent
 * layouts inside the SAME app would be worse.
 *
 * Pure, so the arithmetic that decides what a film looks like is testable without
 * a canvas or a codec.
 */
import { resolveCollage, type PixelRect } from "./collage-layout";
import {
  clipMediaKind,
  collageItems,
  elementDurationMs,
  elementIsRenderable,
} from "./project-document";
import {
  documentTimeline,
  type CardBackground,
  type ClipId,
  type ClipRecord,
  type CollageLayout,
  type ElementId,
  type ElementKind,
  type ProjectDocument,
  type ProjectElement,
  type TextOverlay,
} from "./types";

/**
 * One cell of a planned element, and where it sits in the frame.
 *
 * Usually a piece of media. A COLLAGE's cells are elements, so one of them can
 * be a card instead — which is why `clipId` is optional and `card` exists. A
 * cell has exactly one of the two; a cell that could draw neither is not
 * emitted at all.
 */
export interface PlannedItem {
  /** Absent on a card cell, which draws words rather than media. */
  clipId?: ClipId;
  /** Absent on a card cell, for the same reason. */
  mediaKind?: "video" | "picture";
  /** Destination rectangle in the output frame. */
  rect: PixelRect;
  /** Kept window against the source file. Zero-length for a picture. */
  trimStartMs: number;
  trimEndMs: number;
  /**
   * A card drawn into this cell.
   *
   * Only a collage produces one: everywhere else a card IS the element and
   * fills the frame (`PlannedElement.drawsCard`).
   */
  card?: { contentMd: string; cardBackground?: CardBackground };
  /**
   * The element this cell came from, on a collage.
   *
   * Carried so a caller can say WHICH item it could not draw — a manifest that
   * has to leave a cell out must be able to name it.
   */
  sourceElementId?: string;
}

/**
 * One element on the output timeline.
 *
 * `startMs`/`endMs` are output time. `items` is empty for a text element, which
 * draws its card from `contentMd` instead.
 */
export interface PlannedElement {
  elementId: string;
  sectionId: string;
  sectionName: string;
  kind: ElementKind;
  startMs: number;
  endMs: number;
  durationMs: number;
  items: PlannedItem[];
  /**
   * The arrangement its cells are in. Geometry is already in each item's
   * `rect`; this travels so a PREVIEW can re-resolve the same arrangement at
   * its own pixel size rather than scaling the output rects down.
   */
  layout?: CollageLayout;
  /** Markdown, for a text card or an element's description. */
  contentMd?: string;
  /** What a card is drawn on — a colour, a project picture, or both. */
  cardBackground?: CardBackground;
  /** True when this element draws a card rather than media: text, and voice. */
  drawsCard: boolean;
  /**
   * Words drawn over this element's picture, if it has any.
   *
   * Travels on the plan so every surface that composites a frame from the plan —
   * the export, the previews, the playback canvas — reaches it the same way,
   * without any of them going back to the document for one more field.
   */
  overlay?: TextOverlay;
}

export interface DocumentPlan {
  elements: PlannedElement[];
  totalMs: number;
  frame: { width: number; height: number };
  /** Elements left out of the film, for the caller to report. */
  skipped: { elementId: string; reason: "empty" | "hidden" }[];
}

export interface PlanInput {
  document: ProjectDocument;
  clips: readonly ClipRecord[];
  /** Output frame. Callers usually derive it from the clips. */
  frame: { width: number; height: number };
  /** Project-level fallback hold for pictures and text cards. */
  pictureHoldMs?: number;
}

/**
 * Lay the document out end to end.
 *
 * Elements are SEQUENTIAL — every element occupies its own stretch of the film,
 * in section order then element order. That is what makes "everything renders"
 * work: a text card and a voice card take picture time exactly as a clip does,
 * so there are no gaps and no overlaps to reconcile.
 *
 * The project's positioned audio lane is NOT part of this. That lane is parallel
 * by design (a music bed under everything, a voiceover across several elements),
 * and folding it in here would force it to be sequential too.
 */
export function planDocument(input: PlanInput): DocumentPlan {
  const clipsById = new Map(input.clips.map((clip) => [clip.id, clip]));
  // Every element, INCLUDING the ones kept out of the film: a collage's cells
  // are usually hidden elements, so resolving them off the timeline would leave
  // every collage empty.
  const elementsById = new Map<ElementId, ProjectElement>(
    input.document.elements.map((element) => [element.id, element]),
  );
  const sectionNames = new Map(
    input.document.sections.map((s) => [s.id, s.name]),
  );
  const elements: PlannedElement[] = [];
  const skipped: DocumentPlan["skipped"] = [];
  let cursor = 0;

  for (const element of documentTimeline(input.document)) {
    if (element.hiddenFromFilm) {
      // Kept in the project, kept out of the film — a picture that exists to
      // sit behind a card's words rather than to be a shot. Reported as skipped
      // like anything else that does not make the cut, so a caller counting
      // what was left out sees it.
      skipped.push({ elementId: element.id, reason: "hidden" });
      continue;
    }
    if (!elementIsRenderable(element, clipsById, elementsById)) {
      // A placeholder someone added and never filled must not become a beat of
      // black in the middle of a film.
      skipped.push({ elementId: element.id, reason: "empty" });
      continue;
    }

    const durationMs = elementDurationMs(
      element,
      clipsById,
      input.pictureHoldMs,
      elementsById,
    );
    if (durationMs <= 0) {
      // A video element whose clips are all trimmed to nothing.
      skipped.push({ elementId: element.id, reason: "empty" });
      continue;
    }

    elements.push({
      elementId: element.id,
      sectionId: element.sectionId,
      sectionName: sectionNames.get(element.sectionId) ?? "",
      kind: element.kind,
      startMs: cursor,
      endMs: cursor + durationMs,
      durationMs,
      items: planItems(element, clipsById, input.frame, elementsById),
      ...(element.layout ? { layout: element.layout } : {}),
      ...(element.contentMd ? { contentMd: element.contentMd } : {}),
      ...(element.cardBackground
        ? { cardBackground: element.cardBackground }
        : {}),
      drawsCard: element.kind === "text",
      // Only where there is a picture to label — a card is already words.
      ...(element.overlay?.text.trim() && element.kind !== "text"
        ? { overlay: element.overlay }
        : {}),
    });
    cursor += durationMs;
  }

  return { elements, totalMs: cursor, frame: input.frame, skipped };
}

/** The element's cells, placed in the frame by its layout. */
function planItems(
  element: ProjectElement,
  clipsById: ReadonlyMap<ClipId, ClipRecord>,
  frame: { width: number; height: number },
  elementsById: ReadonlyMap<ElementId, ProjectElement>,
): PlannedItem[] {
  if (element.kind === "collage") {
    return planCollageItems(element, clipsById, frame, elementsById);
  }
  // Missing clips are dropped BEFORE the layout is resolved, so a collage of
  // three whose middle photo was deleted lays out as two rather than as three
  // with a hole.
  const present = element.clipIds
    .map((id) => clipsById.get(id))
    .filter((clip): clip is ClipRecord => clip !== undefined);
  if (present.length === 0) return [];

  const rects = resolveCollage(element.layout, present.length, frame);
  return present.slice(0, rects.length).map((clip, index) => ({
    clipId: clip.id,
    mediaKind: clipMediaKind(clip),
    rect: rects[index]!,
    trimStartMs: Math.max(0, Math.min(clip.trimStartMs, clip.trimEndMs)),
    trimEndMs: Math.max(0, Math.min(clip.trimEndMs, clip.durationMs)),
  }));
}

/**
 * A collage's cells: one per ITEM ELEMENT, in reading order.
 *
 * One cell per item, never one per clip. An item that is itself a multi-clip
 * element contributes its FIRST clip and no more — a four-up of four two-up
 * elements would otherwise be sixteen cells laid out for four, which is not the
 * film that was on screen. It is a real limit rather than an oversight, and the
 * answer to wanting to nest is not to.
 *
 * Items that can draw nothing are dropped BEFORE the layout is resolved, for the
 * same reason a missing clip is: three cells with a hole in the middle is not
 * what three items looked like when it was built.
 */
function planCollageItems(
  element: ProjectElement,
  clipsById: ReadonlyMap<ClipId, ClipRecord>,
  frame: { width: number; height: number },
  elementsById: ReadonlyMap<ElementId, ProjectElement>,
): PlannedItem[] {
  const items = collageItems(element, elementsById).filter((item) =>
    elementIsRenderable(item, clipsById),
  );
  if (items.length === 0) return [];

  const rects = resolveCollage(element.layout, items.length, frame);
  const placed: PlannedItem[] = [];
  items.slice(0, rects.length).forEach((item, index) => {
    const rect = rects[index]!;
    if (item.kind === "text") {
      placed.push({
        rect,
        trimStartMs: 0,
        trimEndMs: 0,
        card: {
          contentMd: item.contentMd ?? "",
          ...(item.cardBackground
            ? { cardBackground: item.cardBackground }
            : {}),
        },
        sourceElementId: item.id,
      });
      return;
    }
    const clip = item.clipIds
      .map((id) => clipsById.get(id))
      .find((entry): entry is ClipRecord => entry !== undefined);
    if (!clip) return;
    placed.push({
      clipId: clip.id,
      mediaKind: clipMediaKind(clip),
      rect,
      trimStartMs: Math.max(0, Math.min(clip.trimStartMs, clip.trimEndMs)),
      trimEndMs: Math.max(0, Math.min(clip.trimEndMs, clip.durationMs)),
      sourceElementId: item.id,
    });
  });
  return placed;
}

/**
 * The output frame a document should render at.
 *
 * The most common size among its VIDEO items wins, because video cannot be
 * re-framed without cropping or bars while a picture is cropped into its cell
 * anyway. With no video at all it falls back to portrait, which is what a phone
 * recorder produces and what every destination expects from one.
 */
export function frameForDocument(
  document: ProjectDocument,
  clips: readonly ClipRecord[],
): { width: number; height: number } {
  const clipsById = new Map(clips.map((clip) => [clip.id, clip]));
  const counts = new Map<
    string,
    { width: number; height: number; count: number }
  >();

  for (const element of documentTimeline(document)) {
    for (const id of element.clipIds) {
      const clip = clipsById.get(id);
      if (!clip || clipMediaKind(clip) !== "video") continue;
      if (!clip.width || !clip.height) continue;
      const key = `${clip.width}x${clip.height}`;
      const entry = counts.get(key);
      if (entry) entry.count += 1;
      else
        counts.set(key, { width: clip.width, height: clip.height, count: 1 });
    }
  }

  let best: { width: number; height: number; count: number } | null = null;
  for (const entry of counts.values()) {
    // Ties break toward the larger frame: upscaling a smaller clip into it costs
    // sharpness, while downscaling the larger one throws detail away for good.
    if (
      !best ||
      entry.count > best.count ||
      (entry.count === best.count &&
        entry.width * entry.height > best.width * best.height)
    ) {
      best = entry;
    }
  }
  return best
    ? { width: best.width, height: best.height }
    : { width: 1080, height: 1920 };
}

/** The planned element covering an output position, if any. */
export function elementAtMs(
  plan: DocumentPlan,
  positionMs: number,
): PlannedElement | null {
  return (
    plan.elements.find(
      (element) => positionMs >= element.startMs && positionMs < element.endMs,
    ) ?? null
  );
}
